/**
 * Lift2Fit — the generated plan result.
 *
 * Day cards with labelled superset blocks, the same prescription formatter The
 * Grid uses, the coach's rationale for every rule, and the plain-English summary
 * tucked into a <details> so the numbers stay the interface.
 */
import { useMemo } from 'react';
import { api, type GeneratedPlan } from '@/lib/api';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Button } from '@/components/ui';
import { ArrowRightIcon, CheckIcon, GridIcon, SparkIcon } from '@/lib/icons';
import { AppLinkButton, EmptyState, LoadingPanel, Panel, PersonaBadge, ScreenHeader, StatusChip } from '../components';
import { ProgrammeDays } from '../programme';
import { trackMeta } from '../demoData';
import { loadGeneratedPlan, useAsync } from '../hooks';
import { Link, navigate } from '../router';

/** Deliberately tiny markdown renderer: headings, bullets and bold only. */
function SummaryMarkdown({ markdown }: { markdown: string }) {
  const blocks = useMemo(() => {
    const lines = markdown.split('\n');
    const out: Array<{ kind: 'heading' | 'bullet' | 'paragraph'; text: string }> = [];
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;
      if (line.startsWith('### ')) out.push({ kind: 'heading', text: line.slice(4) });
      else if (line.startsWith('- ')) out.push({ kind: 'bullet', text: line.slice(2) });
      else out.push({ kind: 'paragraph', text: line });
    }
    return out;
  }, [markdown]);

  const strip = (text: string) => text.replace(/\*\*(.+?)\*\*/g, '$1');

  return (
    <div className="flex flex-col gap-2">
      {blocks.map((block, index) => {
        if (block.kind === 'heading') {
          return (
            <h3 key={index} className="font-display text-[18px] font-black uppercase text-ink">
              {strip(block.text)}
            </h3>
          );
        }
        if (block.kind === 'bullet') {
          return (
            <p key={index} className="flex gap-2 text-[14px] text-ink-muted">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-accent" aria-hidden="true" />
              <span>{strip(block.text)}</span>
            </p>
          );
        }
        return (
          <p key={index} className="text-[14px] text-ink">
            {strip(block.text)}
          </p>
        );
      })}
    </div>
  );
}

export function PlanResultScreen({ planId }: { planId: string }) {
  const { unit } = useUnitPreference();
  const stored = loadGeneratedPlan(planId);
  const state = useAsync(() => api.programme(), [planId]);
  const plan: GeneratedPlan | null = stored ?? state.data ?? null;
  const meta = trackMeta(plan?.track ?? 'general');

  if (state.loading && !plan) {
    return (
      <div className="flex flex-col gap-6">
        <LoadingPanel label="Loading your plan" rows={4} />
        <LoadingPanel label="Loading the day cards" rows={6} />
      </div>
    );
  }

  if (!plan) {
    return (
      <EmptyState
        title="That plan is not on this device"
        body={`We could not find plan ${planId}. Generate a plan and it will be saved to your account.`}
        icon={<SparkIcon className="h-8 w-8 text-accent" />}
        action={
          <Button onClick={() => navigate('/plan/new')}>
            Build a plan
            <ArrowRightIcon />
          </Button>
        }
      />
    );
  }

  const totalExercises = plan.days.reduce((sum, day) => sum + day.exercises.length, 0);
  const totalSets = plan.days.reduce(
    (sum, day) => sum + day.exercises.reduce((daySum, exercise) => daySum + exercise.sets, 0),
    0,
  );

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        eyebrow={`AI plan · ${plan.weeks} weeks`}
        title={plan.name}
        badge={<StatusChip tone="accent">{plan.trackLabel}</StatusChip>}
        lead={`${meta.persona} block. ${plan.daysPerWeek} days a week, ${totalExercises} movements, ${totalSets} working sets per week.`}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <PersonaBadge track={plan.track} />
            <AppLinkButton to="/grid" size="lg">
              Start this session
              <ArrowRightIcon />
            </AppLinkButton>
          </span>
        }
      />

      <div
        className="flex flex-wrap items-center gap-3 border border-success bg-surface px-4 py-3"
        role="status"
        aria-live="polite"
        data-testid="plan-saved"
      >
        <CheckIcon className="text-success" />
        <span className="text-[14px] font-bold text-ink">Saved to your account</span>
        <span className="text-[13px] text-ink-muted">
          Plan <span className="l2f-tabular font-mono">{plan.planId}</span> is yours to keep — generated on your free
          trial or £3.99/month subscription, never a separate purchase.
        </span>
      </div>

      <ProgrammeDays plan={plan} unit={unit} className="lg:grid-cols-2" />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <Panel title="Why these prescriptions" subtitle="Every rule is explainable — deterministic first, prose second.">
          <ul className="flex flex-col divide-y divide-hairline">
            {plan.rationale.map((item) => (
              <li key={item.rule} className="flex flex-col gap-1 py-3">
                <span className="text-[14px] font-bold text-ink">{item.rule}</span>
                <span className="text-[13px] text-ink-muted">{item.evidence}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel title="The coach's summary" subtitle="Plain English, one tap away.">
            <details className="group">
              <summary className="cursor-pointer list-none text-[13px] font-bold uppercase tracking-[0.08em] text-accent">
                Read the full summary
              </summary>
              <div className="mt-3">
                <SummaryMarkdown markdown={plan.summaryMd} />
              </div>
            </details>
          </Panel>

          <Panel title="What next" subtitle="The plan is live on your account the moment you log a set.">
            <ol className="flex flex-col gap-2 text-[13px] text-ink-muted">
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 shrink-0 text-success" />
                Log the first session in The Grid — one tap per set.
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 shrink-0 text-success" />
                Pain 4/10 or above: stop the movement and tell your coach.
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon className="mt-0.5 shrink-0 text-success" />
                The deload lands on week 4 — same movements, less volume.
              </li>
            </ol>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link
                to="/dashboard"
                className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
              >
                <GridIcon />
                Back to dashboard
              </Link>
              <Button variant="ghost" onClick={() => navigate('/plan/new')}>
                Build another plan
              </Button>
            </div>
          </Panel>
        </div>
      </div>

      {state.source === 'demo' ? (
        <p className="text-[12px] text-ink-muted">
          Demo data — start the API to use your own. This block was generated from the intake you just completed.
        </p>
      ) : null}
    </div>
  );
}
